// TEI Embedding Adapter
// Adapter for Text Embeddings Inference

import { createTeiClient } from '../../client/tei/index.js'

const DEFAULT_ENDPOINT = 'http://localhost:8080'

/**
 * Create TEI embedding adapter
 * @param {Function} httpRequest - HTTP request function
 * @returns {Object} TEI adapter with embed, info, isAvailable
 */
export function createTeiAdapter(httpRequest) {
  const teiClient = createTeiClient(httpRequest)

  /**
   * Generate embeddings using TEI
   * @param {Object} params
   * @param {string|string[]} params.texts - Text(s) to embed
   * @param {Object} params.config - Provider config
   * @param {string} params.config.endpoint - TEI endpoint
   * @returns {Promise<number[][]>} Array of embedding vectors
   */
  const embed = async ({ texts, config = {} }) => {
    const { endpoint = DEFAULT_ENDPOINT } = config

    try {
      const embeddings = await teiClient.embed({ texts, endpoint })

      // Normalize: always return array of arrays
      const isString = typeof texts === 'string'
      return isString ? [embeddings] : embeddings
    } catch (error) {
      throw new Error(`TEI embedding failed: ${error.message}`)
    }
  }

  /**
   * Get model information
   * @param {Object} params
   * @param {Object} params.config - Provider config
   * @returns {Promise<Object>}
   */
  const info = async ({ config = {} }) => {
    const { endpoint = DEFAULT_ENDPOINT } = config

    return await teiClient.info({ endpoint })
  }

  /**
   * Check if TEI is available
   * @param {Object} params
   * @param {Object} params.config - Provider config
   * @returns {Promise<boolean>}
   */
  const isAvailable = async ({ config = {} }) => {
    const { endpoint = DEFAULT_ENDPOINT } = config

    return await teiClient.health({ endpoint })
  }

  return { embed, info, isAvailable }
}
